import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';

import {
  baoCaoGetChiTietNhiemVuKhacThanhVienAction,
  baoCaoClearChiTietNhiemVuKhacThanhVien,
} from '@app/store/reducers/baocao';

import { mapTrangThaiKhacLabel, mapMucDoUuTienLabel } from '@app/store/constants';
import { objectShowNumber, rangeDateReport, messageToast } from '@app/utils/define';

const getErrMsg = (error) => {
  if (!error) return '';
  let msg =
    (error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (error && error.title) ||
    '';

  if (!msg && error && error.errors) {
    msg = Object.values(error.errors).flat().join('; ');
  }
  return msg;
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const RANGE_CUSTOM = 'Custom Range';
const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };
  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };
  return { from: toYMD(pair[0]), to: toYMD(pair[1]) };
};

const BaoCaoChiTietNhiemVuKhacThanhVien = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    chiTietNhiemVuKhacThanhVien,
    loadingChiTietNhiemVuKhacThanhVien,
    errorChiTietNhiemVuKhacThanhVien,
  } = useSelector((s) => s.baocao || {});

  const qs = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const nhanVienId = qs.get('nhanVienId') || '';
  const tenThanhVien = qs.get('tenThanhVien') || '';

  const initFrom = qs.get('From') || '';
  const initTo = qs.get('To') || '';

  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [rangeLabel, setRangeLabel] = useState(''); 
  const [fromDate, setFromDate] = useState(initFrom);
  const [toDate, setToDate] = useState(initTo);
  const [appliedFrom, setAppliedFrom] = useState(initFrom);
  const [appliedTo, setAppliedTo] = useState(initTo);


  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (v && v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);
      setFromDate(from);
      setToDate(to);
      setAppliedFrom(from);
      setAppliedTo(to);
    }

    if (!v) {
      setFromDate('');
      setToDate('');
      setAppliedFrom('');
      setAppliedTo('');
    }
  };

  const handleApplyCustomRange = () => {
    if (rangeLabel === RANGE_CUSTOM && !fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }
    setAppliedFrom(fromDate || '');
    setAppliedTo(toDate || '');
    setActivePage(1);
  };

  const handleClearFilter = () => {
    setRangeLabel('');
    setFromDate(initFrom);
    setToDate(initTo);
    setAppliedFrom(initFrom);
    setAppliedTo(initTo);
    setActivePage(1);
  };


  useEffect(() => {
    if (!nhanVienId) return;

    dispatch(
      baoCaoGetChiTietNhiemVuKhacThanhVienAction({
        filter: {
          nhanVienId,
          From: appliedFrom,
          To: appliedTo,
        },
      })
    );
  }, [dispatch, nhanVienId, appliedFrom, appliedTo]);

  // clear khi unmount
  useEffect(() => {
    return () => {
      dispatch(baoCaoClearChiTietNhiemVuKhacThanhVien());
    };
  }, [dispatch]);

  // show error toast
  useEffect(() => {
    if (!errorChiTietNhiemVuKhacThanhVien) return;
    toast.error(
      getErrMsg(errorChiTietNhiemVuKhacThanhVien) ||
        'Không thể tải chi tiết Nhiệm vụ khác của thành viên.'
    );
  }, [errorChiTietNhiemVuKhacThanhVien]);

  const rows = useMemo(() => {
    if (!Array.isArray(chiTietNhiemVuKhacThanhVien)) return [];

    return chiTietNhiemVuKhacThanhVien.map((x) => {
      const status = x?.status ?? x?.Status ?? x?.statusValue ?? x?.StatusValue;
      return {
        id: x?.id ?? x?.Id,
        title: x?.title ?? x?.Title ?? '',
        moTa: x?.moTa ?? x?.MoTa ?? '',
        mucDo: x?.mucDo ?? x?.MucDo,
        status,
        tienDo: x?.tienDo ?? x?.TienDo,
        nguoiYeuCau: x?.nguoiYeuCau ?? x?.NguoiYeuCau ?? '',
      };
    });
  }, [chiTietNhiemVuKhacThanhVien]);

  useEffect(() => {
    setActivePage(1);
  }, [rows.length, pageSize]);

  const totalItems = rows.length;
  const startIndex = (activePage - 1) * pageSize;

  const pagedRows = useMemo(() => {
    return rows.slice(startIndex, startIndex + pageSize);
  }, [rows, startIndex, pageSize]);

  const onChangeShowNumber = (e) => {
    const newSize = parseInt(e.target.value, 10) || 20;
    setPageSize(newSize);
    setActivePage(1);
  };

  const summary = useMemo(() => {
    const total = rows.length;

    const isDone = (st) => {
      if (st === 2 || String(st) === '2') return true;
      const s = String(st ?? '').trim().toLowerCase();
      if (s === 'hoanthanh' || s === 'hoàn thành' || s === 'hoan thanh') return true;
      return false;
    };

    const done = rows.filter((x) => isDone(x.status)).length;
    const doing = total - done;

    return { total, done, doing };
  }, [rows]);

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-start mb-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => history.goBack()}
          title="Quay lại"
        >
          ← Quay lại
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="d-flex flex-column">
            <h3 className="card-title mb-0 float-none">Chi tiết Nhiệm vụ khác</h3>

            {tenThanhVien ? (
              <div className="text-muted mt-1">
                Nhân viên: <b>{tenThanhVien}</b>
              </div>
            ) : (
              <div className="text-muted mt-1">
                {nhanVienId ? (
                  <>
                    Nhân viên ID: <b>{nhanVienId}</b>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className="card-body">
          {/* ✅ Lọc ngày riêng cho Bảng 2 */}
          <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
            <div style={{ minWidth: 220 }}>
              <label className="form-label mb-1">Khoảng thời gian</label>
              <select
                className="form-select form-select-sm"
                value={rangeLabel}
                onChange={handleChangeRange}
                disabled={loadingChiTietNhiemVuKhacThanhVien}
              >
                <option value="">-- Chọn --</option>
                {RANGE_OPTIONS.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>

            {rangeLabel === RANGE_CUSTOM && (
              <>
                <div>
                  <label className="form-label mb-1">Từ ngày</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={loadingChiTietNhiemVuKhacThanhVien}
                  />
                </div>

                <div>
                  <label className="form-label mb-1">Đến ngày</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={loadingChiTietNhiemVuKhacThanhVien}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleApplyCustomRange}
                  disabled={loadingChiTietNhiemVuKhacThanhVien}
                >
                  Lọc
                </button>
              </>
            )}

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClearFilter}
              disabled={loadingChiTietNhiemVuKhacThanhVien}
            >
              Xóa lọc
            </button>

            {(appliedFrom || appliedTo) && (
              <div className="filter-hint" style={{ width: '100%' }}>
                Đang lọc: {fmtDDMMYYYY(appliedFrom) || '...'} → {fmtDDMMYYYY(appliedTo) || '...'}
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between mb-3">
            <form className="form-inline">
              <label className="mr-2">Hiển thị :</label>
              <select className="form-control" value={pageSize} onChange={onChangeShowNumber}>
                {objectShowNumber.map((item, idx) => (
                  <option key={idx} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </select>
            </form>
            <div />
          </div>

          <div className="d-flex justify-content-center mb-3" style={{ gap: 24 }}>
            <div style={{ fontWeight: 700, textAlign: 'center' }}>
              Tổng: <span style={{ color: 'red' }}>{summary.total}</span>
            </div>
            <div style={{ fontWeight: 700, textAlign: 'center' }}>
              Đang thực hiện: <span style={{ color: 'red' }}>{summary.doing}</span>
            </div>
            <div style={{ fontWeight: 700, textAlign: 'center' }}>
              Hoàn thành: <span style={{ color: 'red' }}>{summary.done}</span>
            </div>
          </div>

          {loadingChiTietNhiemVuKhacThanhVien ? (
            <div>Đang tải...</div>
          ) : (
            <>
              <Table striped bordered hover className="align-middle w-100" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '70px' }} />
                  <col style={{ width: '320px' }} />
                  <col style={{ width: '420px' }} />
                  <col style={{ width: '110px' }} />
                  <col style={{ width: '220px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '140px' }} />
                </colgroup>

                <thead className="text-center">
                  <tr>
                    <th>STT</th>
                    <th>Tên nhiệm vụ</th>
                    <th>Nội dung</th>
                    <th>Tiến độ</th>
                    <th>Người yêu cầu</th>
                    <th>Mức độ</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>

                {pagedRows && pagedRows.length > 0 ? (
                  <tbody>
                    {pagedRows.map((x, idx) => {
                      const title = x?.title || '';
                      const nguoiYeuCau = x?.nguoiYeuCau || '';
                      const mucDo = x?.mucDo;
                      const status = x?.status;
                      const moTa = x?.moTa || '';
                      const tienDo = toNum(x?.tienDo);

                      return (
                        <tr key={`${idx}-${x?.id ?? ''}`}>
                          <td className="text-center">{startIndex + idx + 1}</td>

                          <td className="text-center" style={{ whiteSpace: 'normal' }}>
                            <div className="fw-bold">{title}</div>
                          </td>

                          <td className="text-center" style={{ whiteSpace: 'normal' }}>
                            {moTa}
                          </td>

                          <td className="text-center">
                            <b>{tienDo}%</b>
                          </td>

                          <td className="text-center">{nguoiYeuCau}</td>
                          <td className="text-center">{mapMucDoUuTienLabel(mucDo)}</td>
                          <td className="text-center">{mapTrangThaiKhacLabel(status)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan="7" className="text-center">
                        Không có dữ liệu
                      </td>
                    </tr>
                  </tbody>
                )}
              </Table>

              <div className="dataTables_paginate paging_simple_numbers mt-2">
                <Pagination
                  itemClass="page-item"
                  linkClass="page-link"
                  innerClass="pagination pagination-sm no-margin pull-right"
                  activePage={activePage}
                  itemsCountPerPage={Number(pageSize)}
                  totalItemsCount={totalItems}
                  pageRangeDisplayed={5}
                  onChange={(p) => setActivePage(p)}
                  firstPageText="«"
                  prevPageText="‹"
                  nextPageText="›"
                  lastPageText="»"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaoCaoChiTietNhiemVuKhacThanhVien;
